import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import InteractiveMap from "Components/Map/InteractiveMap";
import MapPointList from "Components/Map/MapPointList";
import TypeFilter from "Components/Map/TypeFilter";
import { POINT_TYPES, POINT_TYPE_LIST, sortPoints } from "Components/Map/mapPointTypes";
import { getAllMapPoints } from "services/mapPointService";
import { getAllLocations } from "services/locationService";

/**
 * Read-only, chrome-less version of the map meant to be embedded in an iframe
 * (see public/embed.js). Query params:
 *   theme=light|dark   (default dark)
 *   types=weekly,club,school,simple   (default all)
 *   list=0             hide the list under the map
 *   filter=0           hide the type filter chips
 */
const MapEmbedPage = () => {
  const [searchParams] = useSearchParams();
  const theme = searchParams.get("theme") === "light" ? "light" : "dark";
  const showList = searchParams.get("list") !== "0";
  const showFilter = searchParams.get("filter") !== "0";
  const allowedTypes = useMemo(() => {
    const raw = searchParams.get("types");
    if (!raw) return POINT_TYPE_LIST.map((t) => t.key);
    const keys = raw
      .split(",")
      .map((s) => s.trim())
      .filter((k) => POINT_TYPES[k]);
    return keys.length ? keys : POINT_TYPE_LIST.map((t) => t.key);
  }, [searchParams]);

  const [points, setPoints] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [focus, setFocus] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [fetchedPoints, fetchedLocations] = await Promise.all([
          getAllMapPoints(),
          getAllLocations(),
        ]);
        setPoints(sortPoints(fetchedPoints));
        setLocations(fetchedLocations);
      } catch (err) {
        Sentry.captureException(err);
        console.error("Error loading embedded map:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Paint html/body/#root in the chosen theme so no white/dark edges show in the iframe.
  useEffect(() => {
    const bg = theme === "light" ? "#ffffff" : "#020617";
    const targets = [document.documentElement, document.body, document.getElementById("root")];
    const previous = targets.map((el) => el?.style.background);
    targets.forEach((el) => el && (el.style.background = bg));
    return () => targets.forEach((el, i) => el && (el.style.background = previous[i] || ""));
  }, [theme]);

  // Tell the host page our content height so embed.js can resize the iframe.
  useEffect(() => {
    if (!showList || window.parent === window) return;
    const post = () =>
      window.parent.postMessage(
        { type: "fqst-map:height", height: document.documentElement.scrollHeight },
        "*"
      );
    post();
    const observer = new ResizeObserver(post);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [showList, loading]);

  const locationsById = useMemo(
    () => Object.fromEntries(locations.map((l) => [l.id, l])),
    [locations]
  );
  const basePoints = useMemo(
    () => points.filter((p) => allowedTypes.includes(p.type)),
    [points, allowedTypes]
  );
  const filteredPoints = useMemo(
    () => (typeFilter === "all" ? basePoints : basePoints.filter((p) => p.type === typeFilter)),
    [basePoints, typeFilter]
  );
  const counts = useMemo(() => {
    const c = { all: basePoints.length };
    allowedTypes.forEach((k) => {
      c[k] = basePoints.filter((p) => p.type === k).length;
    });
    return c;
  }, [basePoints, allowedTypes]);

  const handleFocus = (id) => {
    setSelectedId(id);
    setFocus({ id, key: Date.now() });
    mapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const light = theme === "light";
  const wrapper = light ? "bg-white text-slate-900" : "bg-slate-950 text-slate-50";
  const muted = light ? "text-slate-500" : "text-slate-400";

  return (
    <div className={`w-full ${wrapper} ${showList ? "p-3 space-y-3" : "h-screen"}`}>
      {showList && showFilter && allowedTypes.length > 1 && (
        <TypeFilter
          value={typeFilter}
          onChange={setTypeFilter}
          counts={counts}
          theme={theme}
          typeKeys={allowedTypes}
        />
      )}

      <div ref={mapRef} className={showList ? "" : "h-full"}>
        <InteractiveMap
          points={filteredPoints}
          locationsById={locationsById}
          selectedId={selectedId}
          onSelect={setSelectedId}
          focus={focus}
          embed
          className={showList ? "h-[420px]" : "h-full rounded-none"}
        />
      </div>

      {showList && (
        <>
          {loading ? (
            <p className={`text-center py-6 ${muted}`}>Chargement…</p>
          ) : (
            <MapPointList
              points={filteredPoints}
              locationsById={locationsById}
              selectedId={selectedId}
              onFocus={handleFocus}
              embed
              theme={theme}
            />
          )}
          <p className={`text-xs text-right ${muted}`}>
            Carte fournie par la{" "}
            <a
              href={`${window.location.origin}/carte`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-amber-500"
            >
              Fédération Québécoise de Soccer sur Table
            </a>
          </p>
        </>
      )}
    </div>
  );
};

export default MapEmbedPage;
